import { Button } from '@/components/ui/button';
import { Calendar, Phone } from 'lucide-react';
import heroImage from '@/assets/hero-insurance.jpg';
import heroImageWebp from '@/assets/hero-insurance.webp';
import { openCalendlyPopup } from '@/utils/calendly';
import { useEffect, useRef } from 'react';

const Hero = () => {
  const parallaxRef = useRef<HTMLDivElement>(null);

  const handleAppointment = () => {
    openCalendlyPopup();
  };

  // Parallax effect
  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.pageYOffset;
        const rate = scrolled * 0.3; // Positive value for slower downward movement
        parallaxRef.current.style.transform = `translateY(${rate}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToInfo = () => {
    const element = document.getElementById('proces');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="parallax-container relative min-h-[80vh] flex items-center overflow-hidden" aria-labelledby="hero-heading">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0 bg-gradient-hero" aria-hidden="true"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary-dark/90" aria-hidden="true"></div>
      
      {/* Background image with parallax */}
      <div className="parallax-bg absolute inset-0" aria-hidden="true" ref={parallaxRef}>
        <picture>
          <source 
            srcSet={heroImageWebp} 
            type="image/webp"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1335px"
          />
          <img 
            src={heroImage} 
            alt="" 
            width="1335"
            height="752"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1335px"
            loading="eager"
            decoding="sync"
            className="w-full h-full object-cover opacity-30"
            role="presentation"
            fetchPriority="high"
          />
        </picture>
      </div>

      {/* Content */}
      <div className="relative container mx-auto px-4 py-20">
        <div className="max-w-3xl text-white">
          <div className="mb-6">
            <p className="text-accent font-semibold text-lg">
              Persoonlijk, transparant en deskundig verzekeringsadvies
            </p>
          </div>
          
          <h1 id="hero-heading" className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
            Alle verzekeringen onder{' '}
            <span className="text-accent">één dak</span>
          </h1>
          
          <p className="text-xl mb-8 text-white/90 max-w-2xl">
            Bij Compleet Verzekerd adviseren en bemiddelen we in allerlei soorten verzekeringen. 
            De naam zegt het eigenlijk al, je bent compleet verzekerd.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 py-6 text-lg h-14 min-w-[200px] shadow-primary focus:ring-2 focus:ring-accent-foreground focus:ring-offset-2"
              onClick={handleAppointment}
              aria-label="Plan een afspraak"
            >
              <Calendar className="mr-2 w-5 h-5" />
              Plan een afspraak
            </Button>
            <Button 
              asChild
              variant="secondary" 
              size="lg"
              className="bg-background text-foreground hover:bg-background/90 font-semibold px-8 py-6 text-lg h-14 min-w-[200px] shadow-primary focus:ring-2 focus:ring-accent-foreground focus:ring-offset-2"
            >
              <a href="tel:0782200021" className="flex items-center justify-center">
                <Phone className="mr-2 h-5 w-5" />
                078 - 220 00 21
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-6 text-lg h-14 min-w-[200px] focus:ring-2 focus:ring-white focus:ring-offset-2 bg-white/20 backdrop-blur-sm"
              onClick={scrollToInfo}
              aria-label="Meer informatie - spring naar informatie sectie"
            >
              MEER INFORMATIE
            </Button>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;